/**
 * Yeast harvesting and repitch lineage (PLAN-COMPETITIVE C3).
 *
 * Two movements, one record without a movement, and the arithmetic that joins
 * them:
 *
 * ```
 * harvest   batch  → a new stocked lot, gen(batch) + 1     planYeastHarvest
 * take      a lot  → −1 pack, and a record on the batch    planYeastTake
 * take      a pack → a record on the batch, nothing else   planYeastPackTake
 * ```
 *
 * The third is the pack that was never in the cupboard: a vial bought this
 * morning, or a brewer who keeps no inventory at all. Its facts are typed
 * rather than read off a lot, and no stock moves because there is no stock.
 *
 * Taking is the movement and pitching is a stamp on the record it leaves: a
 * starter empties the jar days before the wort is ready, so the cupboard has to
 * move then rather than on brew day. `batch.setYeastPitchedAt` is what finishes
 * the record afterwards, and a brewer who pitches straight from the pack takes
 * and pitches in one press. Harvesting still adds a lot rather than removing
 * one, and is unchanged by any of this.
 *
 * Neither writes anything. They return the payloads a caller dispatches, in the
 * shape `planInventoryDeduction` already established next door and for the same
 * reason: this package has no clock and mints no ids, so a plan is the furthest
 * a pure function can get, and dispatching the pieces as one `dispatchBatch`
 * is what makes the whole movement one entry in the undo stack.
 *
 * ## Where the generation number comes from
 *
 * Nowhere but the batch. `planYeastHarvest` reads `batch.pitchedYeast`, adds
 * one, and that is the entire rule — see {@link nextHarvestGeneration}. The
 * brewer is never asked, because being asked is the bookkeeping the feature
 * exists to abolish, and a number the brewer types is a number that drifts.
 *
 * The consequence worth stating: a harvest off a batch whose pitch was never
 * logged is **generation 1**. That is a claim about the batch ("as far as this
 * log knows, it was pitched fresh"), not a guess, and the brewer can correct it
 * by logging the pitch and harvesting again. It is also the right answer for
 * every batch in every workspace that predates this feature.
 *
 * ## Why a harvest is an `inventory.add` and not a command of its own
 *
 * Because the thing it produces is a stocked lot, and the cupboard already has
 * a command for putting one there. A `batch.harvestYeast` command would be
 * `inventory.add` with a different name, a second inverse to keep honest, and a
 * second place for the availability check to be surprised. What is genuinely
 * new here is the *lineage field* on the item, and that rides on the existing
 * command.
 *
 * The batch, in return, is not told that it was harvested from. It does not
 * need to be: {@link harvestsFromBatch} finds the jars by their back-links,
 * which cannot go stale the way a copy on the batch would when a jar is used up
 * and removed.
 */

import type { BatchDoc, BatchPitchedYeast } from '../document/batch';
import { NAME_MAX_LENGTH } from '../document/common';
import type { RecipeYeast } from '../document/ingredients';
import type { InventoryItemDoc, YeastHarvest } from '../document/inventory';
import {
  buildInventoryItemDoc,
  compareInventoryLots,
  LAB_PACK_GENERATION,
  roundQuantity,
} from '../document/inventory';
import { type InventoryTable, lotMatches } from './availability';
import type { InventoryAdjustment } from './deduct';

/**
 * The generation a harvest off this batch is on: what went in, plus one.
 *
 * A batch with no recorded pitch is treated as having been pitched fresh, so
 * the first harvest off it is generation 1. See this module's header for why
 * that is the honest default rather than an unknown.
 */
export function nextHarvestGeneration(batch: BatchDoc): number {
  return (batch.pitchedYeast?.generation ?? LAB_PACK_GENERATION) + 1;
}

/**
 * The name a culture harvested off this batch should carry.
 *
 * The pitched culture's name if the pitch was logged, otherwise the recipe
 * snapshot's first yeast, otherwise nothing — the caller then has to name it,
 * because this package will not invent `Unknown yeast` and put it on a shelf.
 */
export function harvestedCultureName(batch: BatchDoc): string | null {
  return batch.pitchedYeast?.name ?? batch.recipeSnapshot.yeasts[0]?.name ?? null;
}

/** What the caller has to supply to {@link planYeastHarvest}. */
export interface YeastHarvestRequest {
  /** Id for the new lot. Must not already exist. */
  readonly itemId: string;
  /** The day it came out of the fermenter, `YYYY-MM-DD`. */
  readonly harvestedAt: string;
  /**
   * How many jars, tubes or pouches were put away, in the yeast unit
   * (**packs**). Defaults to 1.
   */
  readonly containers?: number;
  /** The creating envelope's instant, **ms since epoch**. */
  readonly timestamp: number;
  /** Override the name; defaults to {@link harvestedCultureName}. */
  readonly name?: string;
  /** Brewer's note: how it was rinsed, which jar, where it is stored. */
  readonly notes?: string;
}

/** What {@link planYeastHarvest} produces: an `inventory.add` payload. */
export interface YeastHarvestPlan {
  readonly itemId: string;
  /** The complete lot, ready to hand to `inventory.add`. */
  readonly from: InventoryItemDoc;
  /** The generation the new lot is on, restated for the caller's confirmation copy. */
  readonly generation: number;
}

/**
 * Turn "I saved the yeast off this batch" into the lot it produces.
 *
 * Returns `null` when there is no culture to name — a batch whose recipe has no
 * yeast row and whose pitch was never logged. That is the one case the caller
 * must handle rather than the schema, because the fix is a name from the
 * brewer, not an error.
 *
 * ## What the lot inherits, and what it does not
 *
 * It inherits the strain's `catalogRef` and `maker` from the recipe snapshot,
 * so a jar of harvested W-34/70 answers "can I brew this?" for the next lager
 * exactly as a bought sachet would. It does **not** inherit `manufactureDate`:
 * the pack that started this culture was made on a date that describes a
 * population several ferments ago, and carrying it forward would put a stale
 * date next to a fresh one and make {@link yeastViabilityDate} pick a winner
 * for no reason. Slurry decays from its harvest date and from nothing else.
 *
 * It also inherits no `unitCost`. The economics of repitching are the point of
 * the feature and a harvested jar is genuinely free, but "free" and "unpriced"
 * are different claims and only the brewer knows which they mean here.
 */
export function planYeastHarvest(
  batch: BatchDoc,
  request: YeastHarvestRequest,
): YeastHarvestPlan | null {
  const name = request.name ?? harvestedCultureName(batch);
  if (name === null || name.trim() === '') return null;

  const generation = nextHarvestGeneration(batch);
  const strain = batch.recipeSnapshot.yeasts[0];
  const harvest: YeastHarvest = {
    batchId: batch.id,
    batchName: batch.name,
    harvestedAt: request.harvestedAt,
    generation,
  };

  return {
    itemId: request.itemId,
    generation,
    from: buildInventoryItemDoc({
      id: request.itemId,
      kind: 'yeast',
      name,
      quantity: roundQuantity(Math.max(0, request.containers ?? 1)),
      timestamp: request.timestamp,
      catalogRef: strain?.catalogRef ?? null,
      yeastHarvest: harvest,
      ...(request.notes !== undefined && { notes: request.notes }),
    }),
  };
}

/** What {@link planYeastTake} produces: two payloads, dispatched together. */
export interface YeastTakePlan {
  /** The stock movement, in packs. Negative. For `inventory.adjustQuantity`. */
  readonly adjustment: InventoryAdjustment;
  /** The record that goes on the batch. For `batch.setPitchedYeast`. */
  readonly pitchedYeast: BatchPitchedYeast;
}

/** Options for {@link planYeastTake}. */
export interface YeastTakeOptions {
  /** How many packs or jars come out. Defaults to 1, and is capped at what the lot holds. */
  readonly containers?: number;
  /**
   * Whether this jar goes into the wort now as well as leaving the fridge.
   * Defaults to `false`, which is the take a starter begins with.
   */
  readonly pitched?: boolean;
  /** What the movement records as its reason. Defaults to the take or the pitch line. */
  readonly reason?: string;
}

/**
 * The snapshot row this lot answers to, or `null` when the recipe never named it.
 *
 * The recipe is where the pack facts a starter is sized from actually live: a
 * lot records what a brewer owns, and a row records what the strain is — how
 * many cells a pack of it holds, and whether it is liquid or dry. A lot that is
 * not the strain the recipe asked for is a deliberate substitution and gets
 * nothing from the row.
 */
function matchingSnapshotYeast(batch: BatchDoc, item: InventoryItemDoc): RecipeYeast | null {
  return batch.recipeSnapshot.yeasts.find((row) => lotIsRecipeYeast(item, row)) ?? null;
}

/**
 * Turn "I am using this jar on that batch" into the two things that records.
 *
 * Returns `null` for a lot that is not yeast or is empty, so a caller cannot
 * express a take that did not happen. The quantity is **capped at what the lot
 * holds** rather than refused: `inventory.adjustQuantity` will not go below
 * zero, and a take that empties the jar is by far the common case.
 *
 * ## Taking is the movement; pitching is a stamp
 *
 * The jar leaves the fridge days before brew day whenever a starter is built,
 * so that is the moment the stock moves and the moment this plan records. The
 * record it returns carries `pitchedAt: null` unless `pitched` says the jar went
 * straight into the wort, in which case both instants are the same one and the
 * brewer still presses once.
 *
 * ## What the record copies, and from where
 *
 * The lot supplies what a lot knows: its name, its generation, its harvest and
 * manufacture dates, its pack weight, and how many containers came out. The
 * recipe snapshot's matching row supplies what only a strain knows: the form and
 * the cells a pack of it holds. When no row matches, the form is read off the
 * lot — a pack weight is what dry yeast has and slurry does not — and the cell
 * count is left `null` so core applies its own default rather than this module
 * inventing one.
 *
 * The generation on the record is the lot's own, unchanged. Taking does not age
 * a culture; *harvesting* the batch afterwards is what adds one, which is why
 * the increment lives in {@link nextHarvestGeneration} and not here. A bought
 * pack carries `LAB_PACK_GENERATION`.
 */
export function planYeastTake(
  batch: BatchDoc,
  item: InventoryItemDoc,
  at: number,
  options: YeastTakeOptions = {},
): YeastTakePlan | null {
  if (item.kind !== 'yeast' || item.quantity <= 0) return null;

  const wanted = Math.max(0, options.containers ?? 1);
  const taken = roundQuantity(Math.min(wanted, item.quantity));
  if (taken <= 0) return null;

  const pitched = options.pitched === true;
  const fallback = pitched ? `Pitched into "${batch.name}"` : `Taken for "${batch.name}"`;
  const reason = (options.reason ?? fallback).slice(0, 200);
  const row = matchingSnapshotYeast(batch, item);
  const form = row?.form ?? (item.gramsPerPack === null ? 'liquid' : 'dry');

  return {
    adjustment: { itemId: item.id, delta: -taken, reason },
    pitchedYeast: {
      inventoryItemId: item.id,
      name: item.name,
      generation: item.yeastHarvest?.generation ?? LAB_PACK_GENERATION,
      harvestedAt: item.yeastHarvest?.harvestedAt ?? null,
      form,
      packs: taken,
      cellsPerPackBillion: row?.cellsPerPackBillion ?? null,
      gramsPerPack: item.gramsPerPack,
      // The lot's own date is the pack in the hand. A bought pack stocked
      // without one falls back to the date the recipe planned with; a harvested
      // jar never does, because it decays from its harvest, not from a pack.
      manufactureDate:
        item.manufactureDate ??
        (item.yeastHarvest === null ? (row?.manufactureDate ?? null) : null),
      takenAt: at,
      starter: plannedStarter(row, form),
      pitchedAt: pitched ? at : null,
    },
  };
}

/** The pack facts a brewer types when the pack was never in their inventory. */
export interface YeastPackFacts {
  /** What is printed on the vial or the sachet. */
  readonly name: string;
  /** Packaging form, which decides the viability model. */
  readonly form: 'liquid' | 'dry';
  /** How many vials or sachets are going in. */
  readonly packs: number;
  /** The pack's manufacture date, `YYYY-MM-DD`, or `null` when it is not to hand. */
  readonly manufactureDate: string | null;
}

/**
 * Turn "I am using this pack, which was never in the cupboard" into the record.
 *
 * The other half of D-BS1's "a brewer can pitch a pack that was never in
 * inventory". {@link planYeastTake} starts from a stocked lot and cannot help
 * the brewer standing at the bench with a vial bought this morning, or the one
 * who keeps no inventory at all; this starts from what they type. There is no
 * stock movement to return, because there is no stock: the caller dispatches
 * one `batch.setPitchedYeast` and that is the whole change.
 *
 * Returns `null` for a pack with no name and for no packs at all, so a caller
 * cannot express a pitch that did not happen.
 *
 * ## What it copies, and from where
 *
 * The brewer's own words for everything they were asked, and the recipe
 * snapshot's matching row for the two facts nobody types: the cells a pack of
 * the strain holds and what a sachet of it weighs. The row is matched **by
 * name**, trimmed and case-insensitively, because a typed pack has no id and no
 * catalog ref to match on — {@link lotIsRecipeYeast}'s identity rule has
 * nothing to work with here. When no row answers, both are left `null` and core
 * applies its own defaults rather than this module inventing a figure.
 *
 * The generation is always `LAB_PACK_GENERATION`: a pack is a pack, and a jar
 * with a lineage behind it came off a batch and belongs in the cupboard, where
 * the picker can offer it with its generation attached.
 */
export function planYeastPackTake(
  batch: BatchDoc,
  pack: YeastPackFacts,
  at: number,
  options: { readonly pitched?: boolean } = {},
): BatchPitchedYeast | null {
  const name = pack.name.trim().slice(0, NAME_MAX_LENGTH);
  if (name === '') return null;

  const packs = roundQuantity(Math.max(0, pack.packs));
  if (packs <= 0) return null;

  const row = namedSnapshotYeast(batch, name);
  const pitched = options.pitched === true;
  return {
    inventoryItemId: null,
    name,
    generation: LAB_PACK_GENERATION,
    harvestedAt: null,
    form: pack.form,
    packs,
    cellsPerPackBillion: row?.cellsPerPackBillion ?? null,
    gramsPerPack: row?.gramsPerPack ?? null,
    manufactureDate: pack.manufactureDate,
    takenAt: at,
    starter: plannedStarter(row, pack.form),
    pitchedAt: pitched ? at : null,
  };
}

/**
 * The starter the recipe planned for this strain, as the batch's starting point.
 *
 * Amended D-BS3: the batch used to open every culture with no starter, so a
 * brewer who had sized one on the recipe met a blank on brew day and sized it
 * twice. The plan is copied once, at the take, and from then on it is the
 * batch's own record like any other: resize it, or clear it for a jar fresh
 * enough to pitch neat. The recipe's starter is still not consulted again.
 * Never onto dry yeast, which `batch.setYeastStarter` refuses a starter for.
 */
function plannedStarter(
  row: RecipeYeast | null,
  form: 'liquid' | 'dry',
): BatchPitchedYeast['starter'] {
  if (row === null || form === 'dry') return null;
  return row.starter ?? null;
}

/** The snapshot row a typed pack name answers to, or `null`. @see planYeastPackTake */
function namedSnapshotYeast(batch: BatchDoc, name: string): RecipeYeast | null {
  return batch.recipeSnapshot.yeasts.find((row) => sameName(row.name, name)) ?? null;
}

/**
 * Whether two culture names are the same name.
 *
 * Trimmed and folded to lower case, and nothing cleverer: `wlp830` and
 * `WLP830 ` are one strain to any brewer, and a brewer who typed something else
 * meant something else.
 */
function sameName(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * Every jar in the cupboard that came off one batch, oldest lot first.
 *
 * The batch page's "you saved yeast off this" list. Ordered by
 * {@link compareInventoryLots} so it agrees with the order the availability
 * check would consume them in.
 */
export function harvestsFromBatch(
  inventory: InventoryTable,
  batchId: string,
): readonly InventoryItemDoc[] {
  return Object.values(inventory)
    .filter((item) => item.yeastHarvest?.batchId === batchId)
    .sort(compareInventoryLots);
}

/**
 * Whether a stocked lot is the strain a recipe row names.
 *
 * `lotMatches` rather than a name comparison of its own: it is the package's
 * definition of "the same ingredient", so a lot linked to one of the brewer's
 * own yeasts, one carrying catalog provenance and one that only ever had a
 * name all answer here the same way they answer in the cupboard check. A
 * second opinion about identity living in the pitch picker is exactly how the
 * two would come to disagree.
 */
export function lotIsRecipeYeast(lot: InventoryItemDoc, recipeYeast: RecipeYeast | null): boolean {
  if (recipeYeast === undefined || recipeYeast === null) return false;
  return lotMatches(
    lot,
    {
      kind: 'yeast',
      ingredientId: recipeYeast.ingredientId,
      catalogRef: recipeYeast.catalogRef,
      name: recipeYeast.name,
    },
    null,
  );
}

/**
 * Yeast lots that can be pitched, in the order the picker should offer them.
 *
 * Three sorts, outermost first.
 *
 * **The strain the recipe asked for comes first.** The batch already knows it —
 * `harvestedCultureName` reads the same row to name a harvest — and a picker
 * that ignores it makes a brewer with six strains in the fridge hunt for the
 * one the recipe names, with nothing on screen saying which that is. Passing
 * `recipeYeast` is optional and the ordering is all it changes: every stocked
 * lot stays in the list, because the recipe names a *strain* and this question
 * is about a *jar*. A brewer who reached for something else is answering
 * honestly and must not have to fight the control to say so.
 *
 * **Then harvested cultures ahead of bought packs**, because the whole reason a
 * brewer opens this is slurry in the fridge they mean to use before it ages; a
 * bought pack keeps.
 *
 * **Then lot order**, oldest first, as everywhere else.
 */
export function pitchableYeastLots(
  inventory: InventoryTable,
  recipeYeast: RecipeYeast | null = null,
): readonly InventoryItemDoc[] {
  return Object.values(inventory)
    .filter((item) => item.kind === 'yeast' && item.quantity > 0)
    .sort((a, b) => {
      const strain =
        Number(lotIsRecipeYeast(b, recipeYeast)) - Number(lotIsRecipeYeast(a, recipeYeast));
      const harvested = Number(b.yeastHarvest !== null) - Number(a.yeastHarvest !== null);
      return strain || harvested || compareInventoryLots(a, b);
    });
}
